import startOfDay from 'date-fns/startOfDay'
import * as React from 'react'
import { useHistory } from 'react-router'
import * as PromosActions from '../../../../actions/promos'
import { PromoCreateAction, PromoCreation } from '../../../../actions/promos/createPromo'
import type { UploadPromoAction } from '../../../../actions/promos/uploadPromo'
import { useAppDispatch } from '../../../../actions/types'
import { useAppSelector } from '../../../../reducers'
import { Routes } from '../../../../constants'
import type { Promo } from '../../../../types'
import { EstateUtils } from '../../../../utils'
import type{ PromoFormData } from '../types'
import useDisplayBrands from './useDisplayBrands'
import useDisplayTypes from './useDisplayTypes'
import * as Validation from '../validation'
import usePromoParams from '../usePromoParams'
import { PromoUpdateAction } from '../../../../actions/promos/updatePromo'

export default function usePromoFormData(promo?: Promo) {
    const dispatch = useAppDispatch()
    const displayBrands = useDisplayBrands()
    const promoTypes = useDisplayTypes()
    const history = useHistory()
    const { estate } = usePromoParams()
    const isPreprodEstate = useAppSelector(
        (state) => EstateUtils.isPreprodEstate(state, { estate })
    )
    const [promoData, setPromoData] = React.useState<PromoFormData>({
        name: '',
        date_start: startOfDay(new Date()),
        date_end: startOfDay(new Date(Date.now() + 1000 * 60 * 60 * 24 * 7)), // 7 days ahead
        group_brand: null,
        priority: 5,
        exception_countries: [],
        exception_dates: [],
        exception_devices: [],
        exception_price_lt: undefined,
        exception_price_gt: undefined,
        exception_stores: [],
        exception_skus: [],
        group_skus: [],
        type: 'full'
    })
    const [isEditingName, setIsEditingName] = React.useState(false)
    const [saveProgress, setSaveProgress] = React.useState<undefined | number>()
    const [areUnsavedChanges, setUnsavedChanges] = React.useState(false)
    const [formErrors, setFormErrors] = React.useState<Validation.PromoValidation>({})
    const [submitError, setSubmitErrors] = React.useState<Error>()

    React.useEffect(() => {
        const isNew = !promo

        if (isNew) {
            setPromoData(prevPromo => ({
                ...prevPromo,
                type: promoTypes.length > 0
                    ? promoTypes[0].value ?? ''
                    : '',
            }))
            setIsEditingName(true)
        } else {
            setPromoData(prevPromo => ({
                ...prevPromo,
                ...promo,
            }))
        }

    }, [promo, displayBrands, promoTypes])


    const isNew = !promo

    const thereAreErrors =
        (validation: Validation.PromoValidation) => Object.keys(validation).length > 0

    const createPromo = React.useCallback(async (promoData: PromoFormData) => {
        const { promo_img } = promoData
        setSubmitErrors(undefined)
        setSaveProgress(0)

        if (!promo_img) {
            setFormErrors({ promo_img: 'promo-validation-img-empty' })
            setSaveProgress(undefined)
            return
        }
        const createPromoAction: PromoCreateAction = await dispatch(
            PromosActions.createPromo(promoData as PromoCreation)
        )

        if (createPromoAction.type !== 'PROMO_CREATE##SUCCESS') {
            setSubmitErrors(createPromoAction.payload)
            setSaveProgress(undefined)
            return
        }

        const {
            id: promoId,
            filename,
            upload_id: uploadId,
        } = createPromoAction.payload

        const uploadFileAction: UploadPromoAction = await dispatch(
            PromosActions.uploadPromo({
                file: promo_img,
                filename,
                onProgress: setSaveProgress,
                promoId,
                uploadId,
            }),
        )

        if (uploadFileAction.type !== 'UPLOAD_FILE_COMPLETED') {
            setSubmitErrors(uploadFileAction.payload)
            setSaveProgress(undefined)
            return
        }

        await dispatch(PromosActions.completePromoUpload(promoId))

        setSaveProgress(1)

        history.replace(
            Routes.buildRoute(
                Routes.SELF_SERVE_PROMOS,
                { estate, promo: promoId })
        )
    }, [dispatch, estate, history])

    const updatePromo = React.useCallback(async (promoData: PromoFormData) => {
        setSubmitErrors(undefined)
        setSaveProgress(0)

        const updatePromoAction: PromoUpdateAction = await dispatch(
            PromosActions.updatePromo(promoData as Promo)
        )

        if (updatePromoAction.type !== 'PROMO_UPDATE##SUCCESS') {
            setSubmitErrors(updatePromoAction.payload)
            setSaveProgress(undefined)
            return
        }

        setSaveProgress(1)
    }, [dispatch])

    const onSubmit = React.useCallback(() => {
        setFormErrors({})
        setUnsavedChanges(false)

        let errors = isNew
            ? Validation.validateCreation(promoData)
            : Validation.validateUpdate(promoData)

        if (thereAreErrors(errors)) {
            setFormErrors(errors)
            return
        }

        const saveAction = isNew ? createPromo : updatePromo
        const payload = isPreprodEstate
            ? promoData
            : { ...promoData, exception_skus: [] }

        saveAction(payload).then(
            () => setTimeout(() => setSaveProgress(undefined), 5000),
        )
    }, [createPromo, isNew, isPreprodEstate, promoData, updatePromo])

    const resetExceptions = React.useCallback(() => {
        setPromoData(prevPromo => ({
            ...prevPromo,
            exception_countries: [],
            exception_price_lt: undefined,
            exception_price_gt: undefined,
            exception_stores: [],
            exception_skus: [],
        }))
    }, [])

    const hasAnyExclusion = promoData.exception_countries.length > 0
        || promoData.exception_price_lt
        || promoData.exception_price_gt
        || promoData.exception_stores.length > 0
        || promoData.exception_skus.length > 0

    return {
        areUnsavedChanges,
        formErrors,
        hasAnyExclusion,
        isEditingName,
        onSubmit,
        promoData,
        resetExceptions,
        saveProgress,
        setFormErrors,
        setIsEditingName,
        setSubmitErrors,
        setPromoData,
        setUnsavedChanges,
        submitError,
    }
}
